/**
 * Knowledge document ingestion with TEMPORARY (session) vs PERMANENT scope.
 *
 * TRUST layer — Companion master-plan subsystem J. Client for the new
 * knowledge-service endpoints under /api/v1/knowledge/docs:
 *   POST   /knowledge/docs/ingest            chunk+embed+index a doc
 *   GET    /knowledge/docs                    list docs (+scope badges)
 *   DELETE /knowledge/docs/session/{id}      session-end cleanup
 *   DELETE /knowledge/docs/{docId}           remove one doc
 *
 * Temporary docs are TTL-purged server-side (default 24h) and are only
 * retrievable by RAG calls carrying the same session_id.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { get, post, del } from '@/lib/api/client'

export type KnowledgeDocScope = 'permanent' | 'session'

export interface DocIngestRequest {
  title: string
  text: string
  doc_id?: string
  filename?: string
  scope?: KnowledgeDocScope
  session_id?: string
  ttl_hours?: number
  metadata?: Record<string, unknown>
}

export interface DocIngestResponse {
  doc_id: string
  chunks: number
  embedded: number
  scope: KnowledgeDocScope
  session_id?: string | null
  expires_at?: string | null
  namespace: string
  sanitizer_flags: string[]
}

export interface KnowledgeDoc {
  doc_id: string
  title?: string | null
  scope: KnowledgeDocScope
  session_id?: string | null
  expires_at?: string | null
  uploaded_by?: string | null
  ingested_at?: string | null
  chunks: number
}

export interface DocListResponse {
  docs: KnowledgeDoc[]
  total: number
}

const INGEST_TIMEOUT = 120_000

export async function ingestKnowledgeDoc(req: DocIngestRequest): Promise<DocIngestResponse> {
  return post<DocIngestResponse>('/knowledge/docs/ingest', req, { timeout: INGEST_TIMEOUT })
}

export async function listKnowledgeDocs(sessionId?: string): Promise<DocListResponse> {
  const body = await get<DocListResponse>('/knowledge/docs', {
    params: sessionId ? { session_id: sessionId } : undefined,
  })
  return body ?? { docs: [], total: 0 }
}

export async function deleteSessionDocs(sessionId: string): Promise<{ deleted: number }> {
  return del<{ deleted: number }>(`/knowledge/docs/session/${encodeURIComponent(sessionId)}`)
}

export async function deleteKnowledgeDoc(docId: string): Promise<{ deleted: number }> {
  return del<{ deleted: number }>(`/knowledge/docs/${encodeURIComponent(docId)}`)
}

// ── React Query hooks ─────────────────────────────────────────────────────────

const docsKeys = { all: ['knowledge-docs'] as const }

export function useKnowledgeDocs(sessionId?: string, enabled = true) {
  return useQuery({
    queryKey: [...docsKeys.all, sessionId ?? null] as const,
    queryFn: () => listKnowledgeDocs(sessionId),
    enabled,
    staleTime: 15 * 1000,
    retry: false,
  })
}

export function useIngestKnowledgeDoc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ingestKnowledgeDoc,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: docsKeys.all })
    },
  })
}

export function useDeleteSessionDocs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteSessionDocs,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: docsKeys.all })
    },
  })
}
